import {
  Audit,
  ContractLanguage,
  ContractLanguagePrefix,
  getClassificatorValue,
  Network,
  NetworkPrefix,
  ReportType,
  ReportTypePrefix,
  ReportTemplate,
  ReportTemplatePrefix,
  Profile,
} from 'interfaces';

import { getUid, subtractOffsetDate } from 'helpers';

import resolvedIcon from 'images/resolved.svg';
import partiallyFixedIcon from 'images/partiallyFixed.svg';
import acknowledgedIcon from 'images/acknowledged.svg';
import openIcon from 'images/open.png';

const getDefaultAudit = (profile: Profile | null): Audit => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  subtractOffsetDate(now);

  return {
    projectName: '',
    projectUrl: '',
    projectTelegram: '',
    projectTwitter: '',
    projectMedium: '',
    projectGithub: '',
    projectLinkedin: '',
    projectCoinMarketCap: '',
    projectIcon: null,
    slug: '',
    coinCodexSlug: '',
    isPrivate: true,
    isTop: false,
    isDeployed: false,
    networks: [
      {
        network: getClassificatorValue(Network, NetworkPrefix.BSC),
      },
    ],
    projectLanguage: getClassificatorValue(
      ContractLanguage,
      ContractLanguagePrefix.SOLIDITY,
    ),
    centralizationLevel: null,
    projectCentralRisk: null,
    contracts: [],
    dateStart: now,
    dateEnd: now,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    ...getDefaults(profile?.reportType),
  };
};

export default getDefaultAudit;

interface ReportDefaults {
  type: ReportType;
  description: null | {};
  generalCentralRisks: null | {};
  disclaimer: null | {};
  chapters: any[];
  appendixes: any[];
}

const getDefaults = (reportTemplate?: ReportTemplate): ReportDefaults => {
  if (!reportTemplate) {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return defaults[getClassificatorValue(ReportTemplate, ReportTemplatePrefix.HASHEX)];
  }
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return defaults[reportTemplate];
};

const hashexDefaults: ReportDefaults = {
  type: getClassificatorValue(ReportType, ReportTypePrefix.PRO),
  description: {
    time: Date.now(),
    blocks: [
      {
        id: getUid(),
        type: 'paragraph',
        data: {
          text: '{{introduction}}',
        },
      },
      {
        id: getUid(),
        type: 'paragraph',
        data: {
          text: 'The code is available at',
        },
      },
    ],
    version: '2.22.1',
  },
  generalCentralRisks: {
    time: Date.now(),
    blocks: [],
    version: '2.22.1',
  },
  disclaimer: {
    time: Date.now(),
    blocks: [
      {
        type: 'paragraph',
        data: {
          text: 'This is a limited report on our findings based on our analysis, in accordance with good industry practice at the date of this report, in relation to cybersecurity vulnerabilities and issues in the framework and algorithms based on smart contracts, the details of which are set out in this report. In order to get a full view of our analysis, it is crucial for you to read the full report. While we have done our best in conducting our analysis and producing this report, it is important to note that you should not rely on this report and cannot claim against us on the basis of what it says or doesn’t say, or how we produced it, and it is important for you to conduct your own independent investigations before making any decisions. We go into more detail on this in the disclaimer below – please make sure to read it in full.',
        },
      },
      {
        type: 'paragraph',
        data: {
          text: 'By reading this report or any part of it, you agree to the terms of this disclaimer. If you do not agree to the terms, then please immediately cease reading this report, and delete and destroy any and all copies of this report downloaded and/or printed by you. This report is provided for information purposes only and on a non-reliance basis and does not constitute investment advice. No one shall have any right to rely on the report or its contents, and HashEx and its affiliates (including holding companies, shareholders, subsidiaries, employees, directors, officers, and other representatives) (HashEx) owe no duty of care towards you or any other person, nor does HashEx make any warranty or representation to any person on the accuracy or completeness of the report. The report is provided "as is", without any conditions, warranties, or other terms of any kind except as set out in this disclaimer, and HashEx hereby excludes all representations, warranties, conditions, and other terms (including, without limitation, the warranties implied by law of satisfactory quality, fitness for purpose and the use of reasonable care and skill) which, but for this clause, might have effect in relation to the report. Except and only to the extent that it is prohibited by law, HashEx hereby excludes all liability and responsibility, and neither you nor any other person shall have any claim against HashEx, for any amount or kind of loss or damage that may result to you or any other person (including without limitation, any direct, indirect, special, punitive, consequential or pure economic loss or damages, or any loss of income, profits, goodwill, data, contracts, use of money, or business interruption, and whether in delict, tort (including without limitation negligence), contract, breach of statutory duty, misrepresentation (whether innocent or negligent) or otherwise under any claim of any nature whatsoever in any jurisdiction) in any way arising from or connected with this report and the use, inability to use or the results of the use of this report, and any reliance on this report. The analysis of the security is purely based on the smart contracts alone. No applications or operations were reviewed for security. No product code has been reviewed. HashEx owns all copyright rights to the text, images, photographs, and other content provided in the following document. When using or sharing partly or in full, third parties must provide a direct link to the original document mentioning the author (<a href="https://hashex.org">hashex.org</a>).\nHashEx has exclusive rights to publish the results of this audit on company\'s web and social sites.',
        },
      },
    ],
    version: '2.22.3',
  },
  appendixes: [
    {
      title: 'Conclusion',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text: '{{issues-summary}} {{recommendations}}',
            },
          },
        ],
      },
    },
    {
      title: 'Appendix A. Issues severity classification',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Critical.</b> Issues that may cause an unlimited loss of funds or entirely break the contract workflow.  Malicious code (including malicious modification of libraries) is also treated as a critical severity issue. These issues must be fixed before deployments or fixed in already running projects as soon as possible.',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: "<b>High.</b> Issues that may lead to a limited loss of funds, break interaction with users, or other contracts under specific conditions. Also, issues in a smart contract, that allow a privileged account the ability to steal or block other users' funds.",
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Medium.</b> Issues that do not lead to a loss of funds directly, but break the contract logic. May lead to failures in contracts operation.',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Low.</b> Issues that are of a non-optimal code character, for instance, gas optimization tips, unused variables, errors in messages.',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Info.</b> Issues that do not impact the contract operation. Usually, info severity issues are related to code best practices, e.g. style guide.',
            },
          },
        ],
      },
    },
    {
      title: 'Appendix B. Issue status description',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: `<img src="${resolvedIcon}" width="16" /><b> Resolved.</b> The issue has been completely fixed.`,
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: `<img src="${partiallyFixedIcon}" width="16" /><b> Partially fixed.</b> Parts of the issue have been fixed but the issue is not completely resolved.`,
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: `<img src="${acknowledgedIcon}" width="16" /><b> Acknowledged.</b> The team has been notified of the issue, no action has been taken.`,
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: `<img src="${openIcon}" width="16" /><b> Open.</b> The issue remains unresolved.`,
            },
          }
        ],
      },
    },
    {
      title: 'Appendix C. List of examined issue types',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                'Business logic overview',
                'Functionality checks',
                'Following best practices',
                'Access control and authorization',
                'Reentrancy attacks',
                'Front-run attacks',
                'DoS with (unexpected) revert',
                'DoS with block gas limit',
                'Transaction-ordering dependence',
                'ERC/BEP and other standards violation',
                'Unchecked math',
                'Implicit visibility levels',
                'Excessive gas usage',
                'Timestamp dependence',
                'Forcibly sending ether to a contract',
                'Weak sources of randomness',
                'Shadowing state variables',
                'Usage of deprecated code',
              ],
            },
          },
        ],
      },
    },
    {
      title: 'Appendix D. Centralization risks classification',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'header',
            data: {
              text: '<b>Centralization level</b>',
              tag: 'centralizationLevelHeader',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>High.</b> The project owners can manipulate user\'s funds, lock user\'s funds on their will (reversible or irreversible), or maliciously update contracts parameters or bytecode.',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Medium.</b> The project owners can modify contract\'s parameters to break some functions of the project contract or contracts, but user\'s funds remain withdrawable.',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Low.</b> The contract is trustless or its governance functions are safe against a malicious owner.',
            },
          },
          {
            id: getUid(),
            type: 'header',
            data: {
              text: '<b>Centralization risk</b>',
              tag: 'centralizationRiskHeader',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>High.</b> Lost ownership over the project contract or contracts may result in user\'s losses. Contract\'s ownership belongs to EOA or EOAs, and their security model is unknown or out of scope.',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Medium.</b> Contract\'s ownership is transferred to a contract with not industry-accepted parameters, or to a contract without an audit. Also includes EOA with a documented security model, which is out of scope.',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Low.</b> Contract\'s ownership is transferred to a well-known or audited contract with industry-accepted parameters.',
            },
          },
        ],
      },
    }
  ],
  chapters: [],
};

const zeroXGuardDefaults: ReportDefaults = {
  type: getClassificatorValue(ReportType, ReportTypePrefix.NORMAL),
  disclaimer: null,
  description:
  {
    time: Date.now(),
    blocks: [
      {
        id: getUid(),
        type: 'paragraph',
        data: {
          text: '{{introduction}}',
        },
      }
    ],
    version: '2.22.1',
  },
  generalCentralRisks: {
    time: Date.now(),
    blocks: [],
    version: '2.22.1',
  },
  chapters: [
    {
      title: 'Procedure',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: 'We perform our audit according to the following procedure:',
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Automated analysis</b>',
            },
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                "Scanning the project's smart contracts with several publicly available automated&nbsp;Solidity analysis tools",
                'Manual verification (reject or confirm) all the issues found by the tools',
              ],
            },
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Manual audit</b>',
            },
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                'Manually analyze smart contracts for security vulnerabilities',
                "Smart contracts' logic check<br>",
              ],
            },
          },
        ],
        version: '2.22.3',
      },
    },
    {
      title: 'Known vulnerabilities checked',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'table',
            data: {
              withHeadings: true,
              content: [
                ['Title', 'Check result'],
                [
                  "<a href='https://swcregistry.io/docs/SWC-136'>Unencrypted Private Data On-Chain</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-135'>Code With No Effects</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-134'>Message call with hardcoded gas amount</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-129'>Typographical Error</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-128'>DoS With Block Gas Limit</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-131'>Presence of unused variables</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-125'>Incorrect Inheritance Order</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-123'>Requirement Violation</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-120'>Weak Sources of Randomness from Chain Attributes</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-119'>Shadowing State Variables</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-118'>Incorrect Constructor Name</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-116'>Block values as a proxy for time</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-115'>Authorization through tx.origin</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-113'>DoS with Failed Call</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-112'>Delegatecall to Untrusted Callee</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-111'>Use of Deprecated Solidity Functions</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-110'>Assert Violation</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-108'>State Variable Default Visibility</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-107'>Reentrancy</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-106'>Unprotected SELFDESTRUCT Instruction</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-105'>Unprotected Ether Withdrawal</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-104'>Unchecked Call Return Value</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-103'>Floating Pragma</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-102'>Outdated Compiler Version</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-101'>Integer Overflow and Underflow</a>",
                  'passed',
                ],
                [
                  "<a href='https://swcregistry.io/docs/SWC-100'>Function Default Visibility</a>",
                  'passed',
                ],
              ],
            },
          },
        ],
        version: '2.22.3',
      },
    },
  ],
  appendixes: [
    {
      title: 'Conclusion',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text: '{{issues-summary}}',
            },
          },
        ],
      },
    },
    {
      title: 'Disclaimer',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text:
                'This report is subject to the terms and conditions (including without limitation, description of '
                + 'services, confidentiality, disclaimer and limitation of liability) set forth in the Services '
                + 'Agreement, or the scope of services, and terms and conditions provided to the Company in '
                + 'connection with the Agreement. This report provided in connection with the Services set '
                + 'forth in the Agreement shall be used by the Company only to the extent permitted under the '
                + 'terms and conditions set forth in the Agreement. This report may not be transmitted, '
                + 'disclosed, referred to or relied upon by any person for any purposes without 0xGuard prior '
                + 'written consent.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report is not, nor should be considered, an “endorsement” or “disapproval” of any '
                + 'particular project or team. This report is not, nor should be considered, an indication of the '
                + 'economics or value of any “product” or “asset” created by any team or project that contracts '
                + '0xGuard to perform a security assessment. This report does not provide any warranty or '
                + 'guarantee regarding the absolute bug-free nature of the technology analyzed, nor do they '
                + 'provide any indication of the technologies proprietors, business, business model or legal '
                + 'compliance.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report should not be used in any way to make decisions around investment or '
                + 'involvement with any particular project. This report in no way provides investment advice, '
                + 'nor should be leveraged as investment advice of any sort. This report represents an '
                + 'extensive assessing process intending to help our customers increase the quality of their '
                + 'code while reducing the high level of risk presented by cryptographic tokens and blockchain '
                + 'technology.'
                + '\nOxGuard retains exclusive publishing rights for the results of this audit on its website and social networks.',
            },
          },
        ],
      },
    },
  ],
};

const rugdogDefaults: ReportDefaults = {
  type: getClassificatorValue(ReportType, ReportTypePrefix.SIMPLE),
  disclaimer: null,
  description:
  {
    time: Date.now(),
    blocks: [
      {
        id: getUid(),
        type: 'paragraph',
        data: {
          text: '{{introduction}}',
        },
      }
    ],
    version: '2.22.1',
  },
  generalCentralRisks: {
    time: Date.now(),
    blocks: [],
    version: '2.22.1',
  },
  chapters: [
    {
      title: 'Audit Process',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: 'The code was audited by the team according to the following order:'
            }
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Automated analysis</b>'
            }
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                "Scanning the project's smart contracts with several publicly available automated Solidity analysis tools",
                'Manual confirmation of all the issues found by the tools'
              ]
            }
          },
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: '<b>Manual audit</b>'
            }
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                'Thorough manual analysis of smart contracts for security vulnerabilities',
                "Smart contracts' logic check<br>"
              ]
            }
          }
        ],
        version: '2.22.3'
      }
    },
    {
      title: 'Attacks checked',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'table',
            data: {
              withHeadings: true,
              content: [
                ['Title', 'Check result'],
                ["<a href='https://swcregistry.io/docs/SWC-136'>Unencrypted Private Data On-Chain</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-135'>Code With No Effects</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-134'>Message call with hardcoded gas amount</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-129'>Typographical Error</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-128'>DoS With Block Gas Limit</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-131'>Presence of unused variables</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-125'>Incorrect Inheritance Order</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-123'>Requirement Violation</a>", 'passed'],
                [
                  "<a href='https://swcregistry.io/docs/SWC-120'>Weak Sources of Randomness from Chain Attributes</a>",
                  'passed'
                ],
                ["<a href='https://swcregistry.io/docs/SWC-119'>Shadowing State Variables</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-118'>Incorrect Constructor Name</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-116'>Block values as a proxy for time</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-115'>Authorization through tx.origin</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-113'>DoS with Failed Call</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-112'>Delegatecall to Untrusted Callee</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-111'>Use of Deprecated Solidity Functions</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-110'>Assert Violation</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-108'>State Variable Default Visibility</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-107'>Reentrancy</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-106'>Unprotected SELFDESTRUCT Instruction</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-105'>Unprotected Ether Withdrawal</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-104'>Unchecked Call Return Value</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-103'>Floating Pragma</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-102'>Outdated Compiler Version</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-101'>Integer Overflow and Underflow</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-100'>Function Default Visibility</a>", 'passed']
              ]
            }
          }
        ],
        version: '2.22.3'
      }
    }
  ],
  appendixes: [
    {
      title: 'Conclusion',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text: '{{issues-summary}}',
            },
          },
        ],
      },
    },
    {
      title: 'Disclaimer',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text:
                'This report is subject to the terms and conditions (including without limitation, description of '
                + 'services, confidentiality, disclaimer and limitation of liability) set forth in the Services '
                + 'Agreement, or the scope of services, and terms and conditions provided to the Company in '
                + 'connection with the Agreement. This report provided in connection with the Services set '
                + 'forth in the Agreement shall be used by the Company only to the extent permitted under the '
                + 'terms and conditions set forth in the Agreement. This report may not be transmitted, '
                + 'disclosed, referred to or relied upon by any person for any purposes without RugDog prior '
                + 'written consent.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report is not, nor should be considered, an “endorsement” or “disapproval” of any '
                + 'particular project or team. This report is not, nor should be considered, an indication of the '
                + 'economics or value of any “product” or “asset” created by any team or project that contracts '
                + 'RugDog to perform a security assessment. This report does not provide any warranty or '
                + 'guarantee regarding the absolute bug-free nature of the technology analyzed, nor do they '
                + 'provide any indication of the technologies proprietors, business, business model or legal '
                + 'compliance.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report should not be used in any way to make decisions around investment or '
                + 'involvement with any particular project. This report in no way provides investment advice, '
                + 'nor should be leveraged as investment advice of any sort. This report represents an '
                + 'extensive assessing process intending to help our customers increase the quality of their '
                + 'code while reducing the high level of risk presented by cryptographic tokens and blockchain '
                + 'technology.'
                + '\nThe rights to publish the results of this audit are exclusively retained by RugDog.',
            },
          },
        ],
      },
    },
  ]
};

const rapidLabsDefaults: ReportDefaults = {
  type: getClassificatorValue(ReportType, ReportTypePrefix.SIMPLE),
  disclaimer: null,
  description:
  {
    time: Date.now(),
    blocks: [
      {
        id: getUid(),
        type: 'paragraph',
        data: {
          text: '{{introduction}}',
        },
      }
    ],
    version: '2.22.1',
  },
  generalCentralRisks: {
    time: Date.now(),
    blocks: [],
    version: '2.22.1',
  },
  chapters: [
    {
      title: 'Audit Process',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: 'Our audit structure consists of two stages:'
            }
          },
          {
            id: getUid(),
            type: 'paragraph',
            isListHeader: true,
            data: {
              text: '<b>Auto-analysis</b>'
            }
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                'Our automated tools allow us to scan smart contract code and find potential issues',
                'We hand pick and verify all the issues found by the tools'
              ]
            }
          },
          {
            id: getUid(),
            type: 'paragraph',
            isListHeader: true,
            data: {
              text: '<b>Expert audit</b>'
            }
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                'Manual analysis of potential issues and vulnerabilities',
                'Contract code is reviewed thoroughly<br>'
              ]
            }
          }
        ],
        version: '2.22.3'
      }
    },
    {
      title: 'Known issues checked',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'table',
            data: {
              withHeadings: true,
              content: [
                ['Title', 'Result'],
                ["<a href='https://swcregistry.io/docs/SWC-136'>Unencrypted Private Data On-Chain</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-135'>Code With No Effects</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-134'>Message call with hardcoded gas amount</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-129'>Typographical Error</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-128'>DoS With Block Gas Limit</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-131'>Presence of unused variables</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-125'>Incorrect Inheritance Order</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-123'>Requirement Violation</a>", 'passed'],
                [
                  "<a href='https://swcregistry.io/docs/SWC-120'>Weak Sources of Randomness from Chain Attributes</a>",
                  'passed'
                ],
                ["<a href='https://swcregistry.io/docs/SWC-119'>Shadowing State Variables</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-118'>Incorrect Constructor Name</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-116'>Block values as a proxy for time</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-115'>Authorization through tx.origin</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-113'>DoS with Failed Call</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-112'>Delegatecall to Untrusted Callee</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-111'>Use of Deprecated Solidity Functions</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-110'>Assert Violation</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-108'>State Variable Default Visibility</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-107'>Reentrancy</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-106'>Unprotected SELFDESTRUCT Instruction</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-105'>Unprotected Ether Withdrawal</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-104'>Unchecked Call Return Value</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-103'>Floating Pragma</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-102'>Outdated Compiler Version</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-101'>Integer Overflow and Underflow</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-100'>Function Default Visibility</a>", 'passed']
              ]
            }
          }
        ],
        version: '2.22.3'
      }
    }
  ],
  appendixes: [
    {
      title: 'Conclusion',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text: '{{issues-summary}}',
            },
          },
        ],
      },
    },
    {
      title: 'Disclaimer',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text:
                'This report is subject to the terms and conditions (including without limitation, description of '
                + 'services, confidentiality, disclaimer and limitation of liability) set forth in the Services '
                + 'Agreement, or the scope of services, and terms and conditions provided to the Company in '
                + 'connection with the Agreement. This report provided in connection with the Services set '
                + 'forth in the Agreement shall be used by the Company only to the extent permitted under the '
                + 'terms and conditions set forth in the Agreement. This report may not be transmitted, '
                + 'disclosed, referred to or relied upon by any person for any purposes without RapidLabs prior '
                + 'written consent.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report is not, nor should be considered, an “endorsement” or “disapproval” of any '
                + 'particular project or team. This report is not, nor should be considered, an indication of the '
                + 'economics or value of any “product” or “asset” created by any team or project that contracts '
                + 'RapidLabs to perform a security assessment. This report does not provide any warranty or '
                + 'guarantee regarding the absolute bug-free nature of the technology analyzed, nor do they '
                + 'provide any indication of the technologies proprietors, business, business model or legal '
                + 'compliance.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report should not be used in any way to make decisions around investment or '
                + 'involvement with any particular project. This report in no way provides investment advice, '
                + 'nor should be leveraged as investment advice of any sort. This report represents an '
                + 'extensive assessing process intending to help our customers increase the quality of their '
                + 'code while reducing the high level of risk presented by cryptographic tokens and blockchain '
                + 'technology.',
            },
          },
        ],
      },
    },
  ]
};

const rugFreeDefaults: ReportDefaults = {
  type: getClassificatorValue(ReportType, ReportTypePrefix.SIMPLE),
  disclaimer: null,
  description:
  {
    time: Date.now(),
    blocks: [
      {
        id: getUid(),
        type: 'paragraph',
        data: {
          text: '{{introduction}}',
        },
      }
    ],
    version: '2.22.1',
  },
  generalCentralRisks: {
    time: Date.now(),
    blocks: [],
    version: '2.22.1',
  },
  chapters: [
    {
      title: 'Procedure',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'paragraph',
            data: {
              text: 'We perform our audit according to the following procedure:'
            }
          },
          {
            id: getUid(),
            type: 'paragraph',
            isListHeader: true,
            data: {
              text: '<b>Automated analysis</b>'
            }
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                "Scanning the project's smart contracts with several publicly available automated Solidity analysis tools",
                'Manual verification (reject or confirm) all the issues found by the tools'
              ]
            }
          },
          {
            id: getUid(),
            type: 'paragraph',
            isListHeader: true,
            data: {
              text: '<b>Manual audit</b>'
            }
          },
          {
            id: getUid(),
            type: 'list',
            data: {
              style: 'unordered',
              items: [
                'Manually analyse smart contracts for security vulnerabilities',
                "Smart contracts' logic check<br>"
              ]
            }
          }
        ],
        version: '2.22.3'
      }
    },
    {
      title: 'Attack patterns checked',
      body: {
        time: Date.now(),
        blocks: [
          {
            id: getUid(),
            type: 'table',
            data: {
              withHeadings: true,
              content: [
                ['Title', 'Outcome'],
                ["<a href='https://swcregistry.io/docs/SWC-136'>Unencrypted Private Data On-Chain</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-135'>Code With No Effects</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-134'>Message call with hardcoded gas amount</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-129'>Typographical Error</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-128'>DoS With Block Gas Limit</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-131'>Presence of unused variables</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-125'>Incorrect Inheritance Order</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-123'>Requirement Violation</a>", 'passed'],
                [
                  "<a href='https://swcregistry.io/docs/SWC-120'>Weak Sources of Randomness from Chain Attributes</a>",
                  'passed'
                ],
                ["<a href='https://swcregistry.io/docs/SWC-119'>Shadowing State Variables</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-118'>Incorrect Constructor Name</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-116'>Block values as a proxy for time</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-115'>Authorization through tx.origin</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-113'>DoS with Failed Call</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-112'>Delegatecall to Untrusted Callee</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-111'>Use of Deprecated Solidity Functions</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-110'>Assert Violation</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-108'>State Variable Default Visibility</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-107'>Reentrancy</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-106'>Unprotected SELFDESTRUCT Instruction</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-105'>Unprotected Ether Withdrawal</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-104'>Unchecked Call Return Value</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-103'>Floating Pragma</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-102'>Outdated Compiler Version</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-101'>Integer Overflow and Underflow</a>", 'passed'],
                ["<a href='https://swcregistry.io/docs/SWC-100'>Function Default Visibility</a>", 'passed']
              ]
            }
          }
        ],
        version: '2.22.3'
      }
    }
  ],
  appendixes: [
    {
      title: 'Conclusion',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text: '{{issues-summary}}',
            },
          },
        ],
      },
    },
    {
      title: 'Disclaimer',
      body: {
        time: Date.now(),
        blocks: [
          {
            type: 'paragraph',
            data: {
              text:
                'This report is subject to the terms and conditions (including without limitation, description of '
                + 'services, confidentiality, disclaimer and limitation of liability) set forth in the Services '
                + 'Agreement, or the scope of services, and terms and conditions provided to the Company in '
                + 'connection with the Agreement. This report provided in connection with the Services set '
                + 'forth in the Agreement shall be used by the Company only to the extent permitted under the '
                + 'terms and conditions set forth in the Agreement. This report may not be transmitted, '
                + 'disclosed, referred to or relied upon by any person for any purposes without RugFree prior '
                + 'written consent.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report is not, nor should be considered, an “endorsement” or “disapproval” of any '
                + 'particular project or team. This report is not, nor should be considered, an indication of the '
                + 'economics or value of any “product” or “asset” created by any team or project that contracts '
                + 'RugFree to perform a security assessment. This report does not provide any warranty or '
                + 'guarantee regarding the absolute bug-free nature of the technology analyzed, nor do they '
                + 'provide any indication of the technologies proprietors, business, business model or legal '
                + 'compliance.',
            },
          },
          {
            type: 'paragraph',
            data: {
              text:
                'This report should not be used in any way to make decisions around investment or '
                + 'involvement with any particular project. This report in no way provides investment advice, '
                + 'nor should be leveraged as investment advice of any sort. This report represents an '
                + 'extensive assessing process intending to help our customers increase the quality of their '
                + 'code while reducing the high level of risk presented by cryptographic tokens and blockchain '
                + 'technology.',
            },
          },
        ],
      },
    },
  ]
};
// @ts-ignore
const defaults: { [x in ReportTemplate]: ReportDefaults } = {
  [getClassificatorValue(ReportTemplate, ReportTemplatePrefix.HASHEX)]:
    hashexDefaults,
  [getClassificatorValue(ReportTemplate, ReportTemplatePrefix['0XGUARD'])]:
    zeroXGuardDefaults,
  [getClassificatorValue(ReportTemplate, ReportTemplatePrefix.RUGDOG)]:
    rugdogDefaults,
  [getClassificatorValue(ReportTemplate, ReportTemplatePrefix.RAPID_LABS)]:
    rapidLabsDefaults,
  [getClassificatorValue(ReportTemplate, ReportTemplatePrefix.RUGFREE)]:
    rugFreeDefaults,
};
